import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import tenantPaymentService from '../services/tenantPaymentService';
import { track } from '../observability';
import TenantImpersonationBanner from '../components/TenantImpersonationBanner';
import StripeTrustBadge from '../components/StripeTrustBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';
const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

const SetupForm = ({ isUpdate, onDone }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.href },
    });

    if (confirmError) {
      setError(confirmError.message || 'Could not save card. Please try again.');
      setSubmitting(false);
      return;
    }
    // The webhook (setup_intent.succeeded) enables autopay server-side.
    track('tenant_autopay_enabled');
    onDone();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Which methods may be saved is the facility's Stripe dashboard
          setting (Settings -> Payment methods on the connected account);
          the backend no longer pins a list (2026-10-20). The order below
          only applies to what the dashboard allows. ACH returns are handled
          automatically: balance restored + NSF fee + tenant notice. */}
      <PaymentElement options={{
        paymentMethodOrder: ['card', 'us_bank_account', 'cashapp'],
      }} />
      <StripeTrustBadge size="standard" />
      {error && <p className="text-[13px] text-red-500">{error}</p>}
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full rounded-lg bg-blue-600 text-white text-[13px] font-medium py-2.5 hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        {submitting ? 'Saving…' : isUpdate ? 'Save new card' : 'Save card & enable autopay'}
      </button>
    </form>
  );
};

const PortalAutopay = () => {
  const navigate = useNavigate();
  // ?update=1 -> the tenant already has autopay on and is REPLACING the
  // saved card in place (no disable+re-enroll dance). Same SetupIntent
  // flow; the webhook overwrites the saved payment method.
  const [searchParams] = useSearchParams();
  const isUpdate = searchParams.get('update') === '1';
  const [clientSecret, setClientSecret] = useState(null);
  const [connectedAccount, setConnectedAccount] = useState(null);
  const [facilityPublishableKey, setFacilityPublishableKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [facilityName, setFacilityName] = useState(null);

  // Header-brand the autopay setup page by the tenant's own facility.
  useEffect(() => {
    const token = localStorage.getItem('tenantAccessToken');
    if (!token) return;
    axios
      .get(`${API_URL}/api/tenant/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setFacilityName(res.data?.facility_name || null))
      .catch(() => { /* best-effort */ });
  }, []);

  // Same dual-path init as PortalPay: a facility with its OWN publishable
  // key gets standalone Stripe.js (pk + SetupIntent from the same account);
  // otherwise fall back to the legacy shared platform key + {stripeAccount}.
  const stripePromise = useMemo(() => {
    if (facilityPublishableKey) {
      return loadStripe(facilityPublishableKey);
    }
    if (!connectedAccount || !PUBLISHABLE_KEY) return null;
    return loadStripe(PUBLISHABLE_KEY, { stripeAccount: connectedAccount });
  }, [facilityPublishableKey, connectedAccount]);

  const start = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantPaymentService.setupAutopay();
      setClientSecret(res.data.client_secret);
      setConnectedAccount(res.data.connected_account);
      setFacilityPublishableKey(res.data.publishable_key || null);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        navigate('/portal');
        return;
      }
      setError(err.response?.data?.detail || 'Could not start autopay setup.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex flex-col bg-[#FAFBFC]">
        <TenantImpersonationBanner />
        <header className="bg-white border-b border-slate-200/80">
          <div className="max-w-5xl mx-auto px-4 py-3">
            <h1 className="text-[15px] font-semibold text-slate-900">{facilityName || 'Tenant Portal'}</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center">
        <div className="text-center mt-4">
          <p className="text-[15px] font-semibold text-slate-900">
            {isUpdate ? 'Card updated' : 'Autopay enabled'}
          </p>
          <p className="text-[13px] text-slate-500 mt-1">
            {isUpdate
              ? 'Your new card is saved and will be used for future autopay charges.'
              : 'Your saved card will be charged automatically when invoices are issued.'}
          </p>
          <button
            onClick={() => navigate('/portal/dashboard')}
            className="mt-4 text-[13px] text-blue-600 hover:underline"
          >
            Back to dashboard
          </button>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      <TenantImpersonationBanner />
      <header className="bg-white border-b border-slate-200/80">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <h1 className="text-[15px] font-semibold text-slate-900">{facilityName || 'Tenant Portal'}</h1>
        </div>
      </header>
      <div className="max-w-md mx-auto px-4 py-10">
        <button
          onClick={() => navigate('/portal/dashboard')}
          className="text-[13px] text-slate-400 hover:text-slate-600 mb-4"
        >
          ← Back
        </button>
        <div className="rounded-xl border border-slate-200/80 bg-white p-6">
          <h1 className="text-[15px] font-semibold text-slate-900 mb-1">
            {isUpdate ? 'Update your card' : 'Set up autopay'}
          </h1>
          <p className="text-[13px] text-slate-500 mb-4">
            {isUpdate
              ? 'Save a new card below — it replaces the one currently on file for autopay.'
              : 'Save a card and we’ll automatically pay your invoices when they’re issued.'}
          </p>

          {!clientSecret ? (
            <>
              {error && <p className="text-[13px] text-red-500 mb-3">{error}</p>}
              <button
                onClick={start}
                disabled={loading}
                className="w-full rounded-lg bg-blue-600 text-white text-[13px] font-medium py-2.5 hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Starting…' : 'Continue'}
              </button>
            </>
          ) : !stripePromise ? (
            // SetupIntent exists but Stripe couldn't initialize (no per-
            // facility publishable key AND no platform key) — surface it
            // instead of an empty card shell.
            <p className="text-[13px] text-amber-600">
              The card form could not load — online payments are not fully
              configured for this site. Please contact the facility.
            </p>
          ) : (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret, appearance: { theme: 'stripe' } }}
            >
              <SetupForm isUpdate={isUpdate} onDone={() => setDone(true)} />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalAutopay;
